import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { SEOHead } from '../components/SEOHead';
import { Calendar, User, ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPage() {
  const { language } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Sample posts for when there are no real posts
  const samplePosts = [
    {
      post_id: '1',
      title: 'Beneficios de aprender español desde pequeños',
      title_en: 'Benefits of Learning Spanish from a Young Age',
      slug: 'beneficios-aprender-espanol',
      excerpt: 'Descubre por qué es importante comenzar el aprendizaje de idiomas en la infancia.',
      excerpt_en: 'Discover why it\'s important to start language learning in childhood.',
      category: 'Educación',
      created_at: new Date().toISOString(),
      featured_image: 'https://images.pexels.com/photos/5905955/pexels-photo-5905955.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      post_id: '2',
      title: 'El currículo español vs el americano',
      title_en: 'Spanish Curriculum vs American Curriculum',
      slug: 'curriculo-espanol-americano',
      excerpt: 'Comparamos los sistemas educativos para entender mejor nuestra metodología.',
      excerpt_en: 'We compare educational systems to better understand our methodology.',
      category: 'Metodología',
      created_at: new Date().toISOString(),
      featured_image: 'https://images.pexels.com/photos/4145190/pexels-photo-4145190.jpeg?auto=compress&cs=tinysrgb&w=800'
    },
    {
      post_id: '3',
      title: 'Preparación para el examen DELE',
      title_en: 'DELE Exam Preparation',
      slug: 'preparacion-examen-dele',
      excerpt: 'Consejos y estrategias para aprobar el examen de certificación DELE.',
      excerpt_en: 'Tips and strategies to pass the DELE certification exam.',
      category: 'DELE',
      created_at: new Date().toISOString(),
      featured_image: 'https://images.pexels.com/photos/4145153/pexels-photo-4145153.jpeg?auto=compress&cs=tinysrgb&w=800'
    }
  ];

  const displayPosts = posts.length > 0 ? posts : samplePosts;

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        titleES="Blog — Colegio ARCE | Artículos sobre Educación en Español"
        titleEN="Blog — Colegio ARCE | Articles on Spanish Education"
        descES="Artículos, recursos y novedades sobre la enseñanza del español para niños hispanohablantes en EE.UU. Consejos pedagógicos, preparación DELE y cultura española."
        descEN="Articles, resources, and news about Spanish language teaching for heritage-speaking children in the US. Pedagogical tips, DELE prep, and Spanish culture."
        path="/blog"
        breadcrumbs={[
          { nameES: 'Inicio', nameEN: 'Home', path: '/' },
          { nameES: 'Blog', nameEN: 'Blog', path: '/blog' }
        ]}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-40 pb-20 px-6 bg-[#F5F5F7]">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm tracking-wide uppercase text-[#E63946] font-semibold mb-4">
            Blog
          </p>
          <h1 className="text-4xl sm:text-5xl font-semibold text-[#1D1D1F] mb-6" data-testid="blog-title">
            {language === 'es' ? 'Noticias y Artículos' : 'News and Articles'}
          </h1>
          <p className="text-lg text-[#86868B]">
            {language === 'es'
              ? 'Recursos, consejos y novedades sobre la enseñanza del español.'
              : 'Resources, tips, and news about Spanish language teaching.'}
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E63946]"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {displayPosts.map((post) => (
                <article 
                  key={post.post_id} 
                  className="card-apple overflow-hidden group"
                  data-testid={`blog-post-${post.slug}`}
                >
                  {post.featured_image && (
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={post.featured_image} 
                        alt={language === 'es' ? post.title : (post.title_en || post.title)}
                        loading="lazy"
                        width="800"
                        height="450"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="px-3 py-1 bg-[#E63946]/10 text-[#E63946] text-xs font-medium rounded-full">
                        {post.category}
                      </span>
                      <span className="flex items-center gap-1 text-xs text-[#86868B]">
                        <Calendar className="h-3 w-3" />
                        {formatDate(post.created_at)}
                      </span>
                    </div>
                    <h2 className="text-lg font-semibold text-[#1D1D1F] mb-3 line-clamp-2">
                      {language === 'es' ? post.title : (post.title_en || post.title)}
                    </h2>
                    <p className="text-sm text-[#86868B] mb-4 line-clamp-3">
                      {language === 'es' ? post.excerpt : (post.excerpt_en || post.excerpt)}
                    </p>
                    <Link 
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center gap-2 text-[#E63946] font-medium text-sm hover:gap-3 transition-all"
                      data-testid={`blog-read-more-${post.slug}`}
                    >
                      {language === 'es' ? 'Leer más' : 'Read more'}
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          )}

          {posts.length === 0 && !loading && (
            <div className="text-center py-12">
              <p className="text-[#86868B]">
                {language === 'es' 
                  ? 'Estos son artículos de ejemplo. El contenido real estará disponible pronto.'
                  : 'These are sample articles. Real content will be available soon.'}
              </p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
